import { styled, useTheme } from '@automata/mui-theme';
import { NumberField } from '@automata/ui';
import { SxStyles } from '@automata/ui/src/types';
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { FC, ReactNode } from 'react';

import { Option } from '../../types';
import { FieldType } from '../../utils';

type BaseProps<TValue> = {
  invalid?: boolean;
  label: string;
  max?: number;
  min?: number;
  options?: Option[];
  placeholder?: string;
  required?: boolean;
  type: FieldType;
  value: TValue;
  onChange: (value: TValue) => void;
};

const TextFieldContainer = styled(TextField)(() => ({
  '& .MuiOutlinedInput-input': {
    height: '40px',
    padding: '0 12px',
  },
}));

const asteriskStyles = (required?: boolean): SxStyles => ({
  ...(required && {
    ':after': {
      color: 'error.main',
      content: '"*"',
      verticalAlign: 'super',
    },
  }),
});

interface FormLabelProps {
  children: ReactNode;
  required?: boolean;
}

const FormLabel: FC<FormLabelProps> = ({ children, required }) => {
  const theme = useTheme();

  const styles: SxStyles = {
    label: {
      ...theme.typography.overline,
      ...asteriskStyles(required),
      mb: 0.5,
      textTransform: 'uppercase',
    },
  };

  return <Typography sx={styles.label}>{children}</Typography>;
};

export const DynamicFormField = <TValue,>({
  invalid,
  label,
  max,
  min,
  options = [],
  placeholder,
  required = false,
  type,
  value,
  onChange,
}: BaseProps<TValue>) => {
  switch (type) {
    case 'checkbox':
      return (
        <FormControlLabel
          sx={asteriskStyles(required)}
          control={
            <Checkbox
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked as TValue)}
            />
          }
          label={label}
        />
      );

    case 'multiselect':
      return (
        <Box>
          <FormLabel required={required}>{label}</FormLabel>
          <Autocomplete
            freeSolo
            multiple
            options={options}
            value={value as (string | Option)[]}
            onChange={(_, newValue) => onChange(newValue as TValue)}
            renderInput={(params) => (
              <TextFieldContainer
                {...params}
                error={invalid}
                placeholder={placeholder}
                required={required}
                variant="outlined"
              />
            )}
          />
        </Box>
      );

    case 'float':
    case 'number':
      return (
        <NumberField
          aria-label={label}
          decimal={type === 'float'}
          error={invalid}
          label={label}
          min={min}
          max={max}
          placeholder={placeholder}
          required={required}
          step={type === 'float' ? 0.1 : 1}
          value={value as string | number}
          onChange={(e) => onChange(e as TValue)}
        />
      );

    default:
      return (
        <Box>
          <FormLabel required={required}>{label}</FormLabel>
          <TextFieldContainer
            aria-label={label}
            fullWidth
            error={invalid}
            placeholder={placeholder}
            required={required}
            type="text"
            value={value as string | number}
            variant="outlined"
            onChange={(e) => onChange(e.target.value as TValue)}
          />
        </Box>
      );
  }
};
